// MyTimeLog.js
import React, { useEffect, useState, Fragment } from "react";
import API from "./api";

// ---------- Helpers ----------
function kmToMiles(km, decimals = 2) {
  const n = Number(km);
  if (!isFinite(n)) return null;
  return Number((n * 0.621371).toFixed(decimals));
}

function fmtHMS(dt) {
  if (!dt) return "-";
  const m = String(dt).match(/(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return "-";
  return `${m[1]}:${m[2]}`;
}
function hmFromDateTime(dt) {
  if (!dt) return "";
  const t = String(dt).split(" ")[1] || dt; // "HH:MM:SS" ili ceo datetime
  const [hh, mm] = t.split(":");
  return hh && mm ? `${hh}:${mm}` : "";
}
function combineDateHM(dateStr, hm) {
  if (!hm) return null;
  // dateStr u formatu YYYY-MM-DD
  return `${dateStr} ${hm}:00`;
}
function fmtTimeRange(scheduled_at, objective_event_end_time) {
  if (!scheduled_at) return "";
  const start = (scheduled_at.split(" ")[1] || "").slice(0, 5);
  if (!objective_event_end_time) return start;
  const end = objective_event_end_time.slice(0, 5);
  return `${start}-${end}`;
}
function toDateObj(str) {
  const d = new Date(str);
  return isNaN(d) ? null : d;
}
function diffMin(a, b) {
  const da = a ? toDateObj(a) : null;
  const db = b ? toDateObj(b) : null;
  if (!da || !db) return null;
  return Math.round((da - db) / 60000);
}
function deltaBadge(mins) {
  if (mins === null) return <span className="text-gray-400">n/a</span>;
  const abs = Math.abs(mins);
  const color = abs <= 2 ? "text-green-600" : abs <= 10 ? "text-yellow-600" : "text-red-600";
  const sign = mins > 0 ? "+" : "";
  return <span className={`font-semibold ${color}`}>{sign}{mins} min</span>;
}
function isSpecialCanceled(visit) {
  const notes = (visit.notes || "").toString();
  return (
    notes.includes("NoShow/withAppointmentConfirmation") ||
    notes.includes("Canceled by BBarent")
  );
}

export default function MyTimeLog() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changed, setChanged] = useState({});          // { [visit_id]: true }
  const [changedFields, setChangedFields] = useState({}); // { [visit_id]: { field: true } }
  const [serverDate, setServerDate] = useState(null);

  // Učitaj serverski datum
  useEffect(() => {
    API.get("/server-time").then((res) => {
      const d = (res.data.serverDate || res.data.serverTime || "").slice(0, 10);
      setServerDate(d || new Date().toISOString().slice(0, 10));
    }).catch(() => setServerDate(new Date().toISOString().slice(0, 10)));
  }, []);

  // Učitaj vizite za današnji server datum
  useEffect(() => {
    if (!serverDate) return;
    setLoading(true);
    API.get("/time-log/my", { params: { date: serverDate } })
      .then((res) => {
        setVisits(res.data.visits || []);
        setChanged({});
        setChangedFields({});
      })
      .finally(() => setLoading(false));
  }, [serverDate]);

  // Pomoćnici za state izmene
  const markChanged = (visit_id, field) => {
    setChanged((c) => ({ ...c, [visit_id]: true }));
    setChangedFields((cf) => ({
      ...cf,
      [visit_id]: { ...(cf[visit_id] || {}), [field]: true },
    }));
  };

  const updateTLField = (visit_id, field, value) => {
    setVisits((vs) =>
      vs.map((v) =>
        v.id === visit_id
          ? { ...v, time_log: { ...(v.time_log || {}), [field]: value } }
          : v
      )
    );
    markChanged(visit_id, field);
  };

  const updateTimeHM = (visit, field, hm) => {
    // Spoji sa datumom vizite; za drive (koji pripada TEKUĆOJ viziti) takođe koristimo njen datum.
    const datePart = String(visit.scheduled_at).slice(0, 10);
    const dt = combineDateHM(datePart, hm);
    updateTLField(visit.id, field, dt);
  };

  // Sortiraj vizite po vremenu
  const sorted = (visits || []).slice().sort((a, b) => a.scheduled_at.localeCompare(b.scheduled_at));

  const handleSave = async () => {
    setSaving(true);
    try {
      for (let i = 0; i < sorted.length; i++) {
        const v = sorted[i];
        if (!changed[v.id]) continue;

        const tl = v.time_log || {};
        const prev = i > 0 ? sorted[i - 1] : null;

        // Pripremi payload samo sa poljima koja su menjana
        const cf = changedFields[v.id] || {};
        const body = {};

        // Subjektivno vreme u okviru vizite
        if (cf.subjective_in_time) body.subjective_in_time = tl.subjective_in_time || null;
        if (cf.subjective_out_time) body.subjective_out_time = tl.subjective_out_time || null;

        // Event start/end (ako je v.type === 'event', ali dozvolimo i na standard — backend može ignorisati)
        if (cf.event_start_time) body.event_start_time = tl.event_start_time || null;
        if (cf.event_end_time) body.event_end_time = tl.event_end_time || null;

        // Drive & break (pripadaju OVOJ viziti)
        if (cf.drive_start_time) body.drive_start_time = tl.drive_start_time || null;
        if (cf.drive_end_time) body.drive_end_time = tl.drive_end_time || null;
        if (cf.miles_driven) body.miles_driven = tl.miles_driven !== "" ? Number(tl.miles_driven) : null;
        if (cf.break_minutes) body.break_minutes = tl.break_minutes !== "" ? Number(tl.break_minutes) : null;
        if (cf.extra_cost) body.extra_cost = tl.extra_cost !== "" ? Number(tl.extra_cost) : null; // NEW

        // Ako je promenjen drive_end_time, recalc ORS
        if (cf.drive_end_time && prev) {
          body.recalc_route = 1;
          body.prev_visit_id = prev.id;
        }

        await API.put(`/time-log/${v.id}`, body);
      }

      // Osveži listu nakon snimanja
      const res = await API.get("/time-log/my", { params: { date: serverDate } });
      setVisits(res.data.visits || []);
      setChanged({});
      setChangedFields({});
      alert("Saved successfully!");
    } catch (err) {
      console.error(err);
      alert("Failed to save!");
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400">Loading...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto pb-32 pt-5 px-2">
      <h2 className="text-lg font-bold mb-5">Today's Time Log</h2>

      {sorted.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-52 text-gray-400 text-lg">
          <span>No visits for today.</span>
        </div>
      ) : (
        <div>
          {sorted.map((v, idx) => {
            const tl = v.time_log || {};
            const prev = idx > 0 ? sorted[idx - 1] : null;

            const isEvent    = v.type === "event";
            const isPI       = isEvent && v.program_type === "PI";
            const isCanceled = v.status === "canceled";
            // Boolean varijante da izbegnemo slučaj da React renderuje "0"
            const allowInterSlot = !isCanceled || isSpecialCanceled(v);

            // Prva vožnja:
            // - non-PI event: ponaša se kao do sada
            // - PI event: samo ako je pay_first_commute = 1
            // - standard: samo ako je pay_first_commute = 1 (već je bilo tako)
            const canShowFirstLeg =
              idx === 0 && (
                (!isPI && isEvent) ||
                v.pay_first_commute === 1
              );

            const showInterSlot =
              allowInterSlot && (Boolean(prev) || canShowFirstLeg);

            // Δ subjektivno vs objektivno
            const dIn = tl.subjective_in_time ? diffMin(tl.subjective_in_time, v.checkin_time) : null;
            const dOut = tl.subjective_out_time ? diffMin(tl.subjective_out_time, v.checkout_time) : null;

            return (
              <Fragment key={v.id}>
                {/* Inter-slot (VOŽNJA I PAUZA) od PRETHODNE vizite ka OVOJ viziti */}
                {showInterSlot && (
                  <div className="rounded-2xl border bg-gray-50 p-3 mb-4">
                    <div className="text-xs tracking-wide text-gray-500 mb-2">
                      Drive & break from{" "}
                      <span className="font-semibold">
                        {prev ? `${prev.student_first_name} ${prev.student_last_name}` : "home"}
                      </span>{" "}
                      →{" "}
                      <span className="font-semibold">
                        {v.student_first_name} {v.student_last_name}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Drive Start</label>
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          className="w-full border rounded-lg px-3 py-2"
                          value={hmFromDateTime(tl.drive_start_time)}
                          onChange={(e) => updateTimeHM(v, "drive_start_time", e.target.value)}
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Drive End</label>
                        <input
                          type="time"
                          lang="en-GB"
                          step="60"
                          className="w-full border rounded-lg px-3 py-2"
                          value={hmFromDateTime(tl.drive_end_time)}
                          onChange={(e) => updateTimeHM(v, "drive_end_time", e.target.value)}
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Miles (teacher)</label>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          className="w-full border rounded-lg px-3 py-2"
                          value={tl.miles_driven ?? ""}
                          onChange={(e) => updateTLField(v.id, "miles_driven", e.target.value)}
                        />
                      </div>

                      {/* Break se NE prikazuje ako je prvi commute koji se ne plaća */}
                      {(prev || !canShowFirstLeg) && (
                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Paid Break (min)</label>
                          <input
                            type="number"
                            min="0"
                            className="w-full border rounded-lg px-3 py-2"
                            value={tl.break_minutes ?? ""}
                            onChange={(e) => updateTLField(v.id, "break_minutes", e.target.value)}
                          />
                        </div>
                      )}

                      {/* Extra cost */}
                      <div>
                        <label className="block text-[11px] text-gray-500 mb-1">Extra cost ($)</label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          className="w-full border rounded-lg px-3 py-2"
                          value={tl.extra_cost ?? ""}
                          onChange={(e) => updateTLField(v.id, "extra_cost", e.target.value)}
                        />
                      </div>

                      <div className="col-span-2">
                        <div className="text-[11px] text-gray-500 mb-1">Route (objective, ORS)</div>
                        <div className="font-mono text-sm">
                          {tl.objective_route_distance_km != null || tl.objective_route_duration_min != null
                            ? (() => {
                                const km = tl.objective_route_distance_km != null ? Number(tl.objective_route_distance_km) : null;
                                const mi = km != null ? kmToMiles(km) : null;
                                const mins = tl.objective_route_duration_min ?? "?";
                                return `${mi ?? "?"} mi • ${mins} min`;
                              })()
                            : "-"}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sam slot vizite (subjektivno + objektivno) */}
                <div
                  className={`rounded-2xl shadow p-4 mb-6 ${
                    v.status === "canceled"
                      ? "bg-purple-50"                      // canceled = ljubičasto
                      : isPI
                      ? "border-l-4 border-orange-400 bg-orange-50" // PI event = narandžasto
                      : isEvent
                      ? "border-l-4 border-yellow-400 bg-yellow-50" // ostali eventi = žuto
                      : "bg-green-50"
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <div className="font-semibold text-base">
                      {v.student_first_name} {v.student_last_name}
                    </div>
                    <div className="font-mono text-lg">
                      {fmtTimeRange(v.scheduled_at, v.event_end_time)}
                    </div>

                    {isPI ? (
                      <span className="ml-2 inline-flex items-center text-orange-700 text-xs font-bold">
                        <svg className="inline-block mr-1" width={18} height={18} viewBox="0 0 24 24">
                          <path fill="currentColor" d="M7 9v6h2V9zm4 0v6h2V9zm4 0v6h2V9z" />
                        </svg>
                        PI
                      </span>
                    ) : isEvent && (
                      <span className="ml-2 inline-flex items-center text-yellow-700 text-xs font-bold">
                        <svg className="inline-block mr-1" width={18} height={18} viewBox="0 0 24 24">
                          <path fill="currentColor" d="M7 9v6h2V9zm4 0v6h2V9zm4 0v6h2V9z" />
                        </svg>
                        Event
                      </span>
                    )}

                    {v.status === "canceled" && (
                      <span
                        className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full
                                   bg-purple-100 text-purple-700 text-xs font-semibold tracking-wide"
                      >
                        Canceled
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Objektivno */}
                    <div>
                      <div className="text-[11px] text-gray-500">Check-in (objective)</div>
                      <div className="font-mono">
                        {fmtHMS((v.checkin_time || "").split(" ")[1] || "")}
                      </div>
                    </div>
                    <div>
                      <div className="text-[11px] text-gray-500">Check-out (objective)</div>
                      <div className="font-mono">
                        {fmtHMS((v.checkout_time || "").split(" ")[1] || "")}
                      </div>
                    </div>

                    {/* Subjektivno (teacher) – edit */}
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">Time-in (teacher)</label>
                      <input
                        type="time"
                        lang="en-GB"
                        step="60"
                        className="w-full border rounded-lg px-3 py-2"
                        value={hmFromDateTime(tl.subjective_in_time)}
                        onChange={(e) => updateTimeHM(v, "subjective_in_time", e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="block text-[11px] text-gray-500 mb-1">Time-out (teacher)</label>
                      <input
                        type="time"
                        lang="en-GB"
                        step="60"
                        className="w-full border rounded-lg px-3 py-2"
                        value={hmFromDateTime(tl.subjective_out_time)}
                        onChange={(e) => updateTimeHM(v, "subjective_out_time", e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Delta prikaz */}
                  <div className="mt-2 text-sm">
                    <span className="text-gray-500 mr-2">Δ in:</span>
                    {deltaBadge(dIn)}
                    <span className="text-gray-500 mx-2">•</span>
                    <span className="text-gray-500 mr-2">Δ out:</span>
                    {deltaBadge(dOut)}
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      )}

      {/* Sticky Save */}
      <div className="fixed bottom-0 left-0 w-full flex justify-center pb-5 z-30 bg-gradient-to-t from-white via-white/80 to-transparent">
        <button
          className="bg-blue-700 text-white px-8 py-3 rounded-full shadow-xl text-lg font-bold hover:bg-blue-900 transition-all disabled:opacity-50"
          onClick={handleSave}
          disabled={saving || Object.keys(changed).length === 0}
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}







